/**
 * Single source of truth for page and component permissions.
 * Used by Add/Edit Role modal and by permission enforcement (nav, route guard, component hiding).
 */

export interface PermissionComponent {
  id: string;
  label: string;
}

export interface PermissionPageConfig {
  pageId: string;
  pageLabel: string;
  components: PermissionComponent[];
}

const FULL_PAGE_ACCESS: PermissionComponent = { id: 'full-page', label: 'Full page access' };

/** All dashboard pages with their component IDs. First component is always Full page access. */
export const PERMISSION_PAGES: PermissionPageConfig[] = [
  {
    pageId: 'command-center',
    pageLabel: 'Command Center',
    components: [
      FULL_PAGE_ACCESS,
      { id: 'net-sales-kpi', label: 'Net Sales KPI' },
      { id: 'labor-cost-kpi', label: 'Labor Cost KPI' },
      { id: 'review-rating-kpi', label: 'Review Rating KPI' },
      { id: 'hourly-net-sales-chart', label: 'Hourly Net Sales Chart' },
      { id: 'labor-cost-percentage-gauge', label: 'Labor Cost Percentage Gauge' },
      { id: 'alerts-financial-labor', label: 'Alerts: Financial & Labor' },
      { id: 'alerts-inventory-supply-chain', label: 'Alerts: Inventory & Supply Chain' },
      { id: 'alerts-reputation-hr', label: 'Alerts: Reputation & HR' },
    ],
  },
  {
    pageId: 'sales-labor-detail',
    pageLabel: 'Sales & Labor Detail',
    components: [
      FULL_PAGE_ACCESS,
      { id: 'kpi-actual-total-net-sales', label: 'Actual Total Net Sales' },
      { id: 'kpi-actual-labor-cost', label: 'Actual Labor Cost' },
      { id: 'kpi-total-hours', label: 'Total Hours' },
      { id: 'kpi-sales-per-man-hour', label: 'Sales Per Man Hour' },
      { id: 'kpi-no-of-transactions', label: 'No. of Transactions' },
      { id: 'kpi-average-check', label: 'Average Check' },
      { id: 'kpi-total-discounts', label: 'Total Discounts' },
      { id: 'kpi-total-refunds', label: 'Total Refunds' },
      { id: 'hourly-breakdown', label: 'Hourly Breakdown' },
      { id: 'sources-of-sales', label: 'Sources of Sales' },
      { id: 'staff-timesheet', label: 'Staff Timesheet' },
      { id: 'daily-targets-vs-actual', label: 'Daily Targets vs Actual' },
    ],
  },
  {
    pageId: 'sales-trend-reports',
    pageLabel: 'Sales Trend Reports',
    components: [
      FULL_PAGE_ACCESS,
      { id: 'trends-chart', label: 'Trends Chart' },
      { id: 'kpis', label: 'KPIs' },
      { id: 'net-sales-by-category', label: 'Net Sales by Category' },
    ],
  },
  {
    pageId: 'kitchen-performance',
    pageLabel: 'Kitchen Performance',
    components: [
      FULL_PAGE_ACCESS,
      { id: 'import-csv', label: 'Import CSV' },
      { id: 'kitchen-performance', label: 'Kitchen Performance' },
    ],
  },
  {
    pageId: 'kitchen-performance-details',
    pageLabel: 'Kitchen Performance Details',
    components: [FULL_PAGE_ACCESS],
  },
  {
    pageId: 'activity-log',
    pageLabel: 'Activity Log',
    components: [FULL_PAGE_ACCESS],
  },
  {
    pageId: 'ratings-and-reviews',
    pageLabel: 'Ratings and Reviews',
    components: [FULL_PAGE_ACCESS],
  },
  {
    pageId: 'inventory-food-cost',
    pageLabel: 'Inventory & Food Cost',
    components: [
      FULL_PAGE_ACCESS,
      { id: 'kpi-current-food-cost', label: 'Current Food Cost' },
      { id: 'kpi-inventory-value', label: 'Inventory Value' },
      { id: 'kpi-waste-cost', label: 'Waste Cost' },
      { id: 'kpi-pending-orders', label: 'Pending Orders' },
      { id: 'cost-of-goods-sold-gauge', label: 'Cost of Goods Sold Gauge' },
      { id: 'food-cost-variance', label: 'Food Cost Variance' },
      { id: 'order-tracker', label: 'Order Tracker' },
    ],
  },
  {
    pageId: 'training-management',
    pageLabel: 'Training Management',
    components: [
      FULL_PAGE_ACCESS,
      { id: 'kpi-office-staff', label: 'Staff in Training' },
      { id: 'kpi-trainings-overdue', label: 'Trainings Overdue' },
      { id: 'kpi-training-completion', label: 'Training Completion' },
      { id: 'employee-training', label: 'Employee Training' },
    ],
  },
  {
    pageId: 'reviews-management',
    pageLabel: 'Reviews Management',
    components: [
      FULL_PAGE_ACCESS,
      { id: 'self-review-completion-chart', label: 'Self Review Completion Chart' },
      { id: 'manager-review-completion-chart', label: 'Manager Review Completion Chart' },
      { id: 'do-review-completion-chart', label: 'DO Review Completion Chart' },
      { id: 'final-review-completion-chart', label: 'Final Review Completion Chart' },
      { id: 'checkin-30-completion-chart', label: '30 Day Check-in Completion Chart' },
      { id: 'checkin-60-completion-chart', label: '60 Day Check-in Completion Chart' },
      { id: 'review-cycles', label: 'Review Cycles' },
      { id: 'past-reviews', label: 'Past Reviews' },
    ],
  },
  {
    pageId: 'disciplinary-management',
    pageLabel: 'Disciplinary Management',
    components: [
      FULL_PAGE_ACCESS,
      { id: 'total-team-members-kpi', label: 'Total Team Members KPI' },
      { id: 'pending-pips-kpi', label: 'Pending PIPs KPI' },
      { id: 'critical-kpi', label: 'Critical KPI' },
      { id: 'disciplinary-records', label: 'Disciplinary Records' },
    ],
  },
  {
    pageId: 'disciplinary-management-details',
    pageLabel: 'Disciplinary Management Details',
    components: [
      FULL_PAGE_ACCESS,
      { id: 'assign-points', label: 'Assign Points' },
      { id: 'employee-card', label: 'Employee Card' },
      { id: 'incident-history-90-days', label: 'Incident History (90 Days)' },
      { id: 'required-protocol', label: 'Required Protocol' },
      { id: 'prior-incidents', label: 'Prior Incidents' },
    ],
  },
  {
    pageId: 'calendar-events',
    pageLabel: 'Calendar & Events',
    components: [
      FULL_PAGE_ACCESS,
      { id: 'add-events', label: 'Add Events' },
      { id: 'calendar', label: 'Calendar' },
      { id: 'upcoming-events', label: 'Upcoming Events' },
    ],
  },
  {
    pageId: 'user-management',
    pageLabel: 'User Management',
    components: [FULL_PAGE_ACCESS],
  },
  {
    pageId: 'rbac-management',
    pageLabel: 'RBAC Management',
    components: [FULL_PAGE_ACCESS],
  },
  {
    pageId: 'goal-setting',
    pageLabel: 'Goal Setting',
    components: [
      FULL_PAGE_ACCESS,
      { id: 'sales-goal', label: 'Sales Goal' },
      { id: 'labor-cost-pct-goal', label: 'Labor Cost % Goal' },
      { id: 'hours-goal', label: 'Hours Goal' },
      { id: 'spmh-goal', label: 'SPMH Goal' },
      { id: 'food-cost-pct-goal', label: 'Food Cost % Goal' },
    ],
  },
  {
    pageId: 'location-management',
    pageLabel: 'Location Management',
    components: [FULL_PAGE_ACCESS],
  },
  {
    pageId: 'training-settings',
    pageLabel: 'Training Settings',
    components: [FULL_PAGE_ACCESS, { id: 'trainings', label: 'Trainings' }],
  },
  {
    pageId: 'review-settings',
    pageLabel: 'Review Settings',
    components: [FULL_PAGE_ACCESS],
  },
  {
    pageId: 'disciplinary-settings',
    pageLabel: 'Disciplinary Settings',
    components: [FULL_PAGE_ACCESS],
  },
  {
    pageId: 'events-notifications-settings',
    pageLabel: 'Events & Notifications',
    components: [FULL_PAGE_ACCESS],
  },
  {
    pageId: 'alerts-notifications-settings',
    pageLabel: 'Alerts & Notifications',
    components: [FULL_PAGE_ACCESS],
  },
  {
    pageId: 'data-sync-settings',
    pageLabel: 'Data sync',
    components: [FULL_PAGE_ACCESS],
  },
];

/** All page IDs in order (for path → pageId mapping). */
export const PERMISSION_PAGE_IDS = PERMISSION_PAGES.map((p) => p.pageId);

/** Get component IDs for a page. */
export function getComponentIdsForPage(pageId: string): string[] {
  const page = PERMISSION_PAGES.find((p) => p.pageId === pageId);
  return page ? page.components.map((c) => c.id) : [];
}

/** Map dashboard path to pageId. /dashboard/disciplinary-management/:id -> disciplinary-management-details. */
export function getPageIdFromPath(path: string): string | null {
  const match = /^\/dashboard\/?(.*)$/.exec(path);
  if (!match) return null;
  const segment = match[1]?.trim() || '';
  if (!segment) return 'command-center';
  const parts = segment.split('/').filter(Boolean);
  const firstSegment = parts[0];
  if (!firstSegment) return 'command-center';
  if (firstSegment === 'disciplinary-management' && parts.length > 1) {
    return 'disciplinary-management-details';
  }
  if (firstSegment === 'kitchen-performance' && parts.length > 1) {
    return 'kitchen-performance-details';
  }
  return firstSegment;
}

/** Check if permissions allow access to the given page. No permissions = allow all (backward compat). */
export function canAccessPage(
  permissions: import('../types/rbac.types').RolePermissions | undefined,
  pageId: string
): boolean {
  if (!permissions) return true;
  if (permissions.type === 'all') return true;
  if (pageId === 'no-access') return true;
  if (pageId === 'profile') return true;
  return permissions.pages?.some((p) => p.pageId === pageId) ?? false;
}

/** True if the user has access to at least one dashboard page (so we can show no-access when false). */
export function hasAccessToAnyPage(
  permissions: import('../types/rbac.types').RolePermissions | undefined
): boolean {
  if (!permissions) return true;
  if (permissions.type === 'all') return true;
  const pages = permissions.pages;
  return Array.isArray(pages) && pages.length > 0;
}

const FULL_PAGE_COMPONENT_ID = 'full-page';

type RolePermissions = import('../types/rbac.types').RolePermissions;
type PagePermission = import('../types/rbac.types').PagePermission;

/**
 * Compute effective permissions (permissions + overrides − removals) so component visibility
 * respects user-specific removals and overrides (e.g. when role is "all" but user has only some components).
 */
export function getEffectivePermissions(
  permissions: RolePermissions | undefined,
  permissionOverrides: RolePermissions | null | undefined,
  permissionRemovals: RolePermissions | null | undefined
): RolePermissions | undefined {
  if (!permissions) return undefined;
  const removalPages = permissionRemovals?.type === 'custom' ? permissionRemovals.pages ?? [] : [];
  const overridePages = permissionOverrides?.type === 'custom' ? permissionOverrides.pages ?? [] : [];

  const isPageFullyRemoved = (pageId: string): boolean => {
    const entry = removalPages.find((p) => p.pageId === pageId);
    return entry != null && (entry.components == null || entry.components.length === 0);
  };

  const getEffectivePage = (pageId: string, pageLabel: string, allComponentIds: string[]): PagePermission | null => {
    if (isPageFullyRemoved(pageId)) return null;
    const removalEntry = removalPages.find((p) => p.pageId === pageId);
    const toRemove = new Set(removalEntry?.components ?? []);

    let baseComponents: string[];
    if (permissions.type === 'all') {
      const overridePage = overridePages.find((p) => p.pageId === pageId);
      baseComponents = (overridePage?.components?.length ? [...overridePage.components] : [...allComponentIds]) as string[];
    } else {
      const page = permissions.pages?.find((p) => p.pageId === pageId);
      if (!page) return null;
      baseComponents = (page.components?.length ? [...page.components] : [...allComponentIds]) as string[];
    }
    const effective = baseComponents.filter((c) => !toRemove.has(c));
    return { pageId, pageLabel, components: effective };
  };

  if (permissions.type === 'all') {
    if (removalPages.length === 0 && overridePages.length === 0) return { type: 'all' };
    const pages: PagePermission[] = [];
    for (const config of PERMISSION_PAGES) {
      const allIds = config.components.map((c) => c.id);
      const effectivePage = getEffectivePage(config.pageId, config.pageLabel, allIds);
      if (effectivePage != null) pages.push(effectivePage);
    }
    return { type: 'custom', pages };
  }

  const pages: PagePermission[] = [];
  for (const page of permissions.pages ?? []) {
    const config = PERMISSION_PAGES.find((p) => p.pageId === page.pageId);
    const allIds = config?.components.map((c) => c.id) ?? [];
    const effectivePage = getEffectivePage(page.pageId, page.pageLabel ?? page.pageId, allIds);
    if (effectivePage != null) pages.push(effectivePage);
  }
  return { type: 'custom', pages };
}

/**
 * Check if permissions allow access to a specific component on a page.
 * Use to conditionally render and fetch only for allowed components.
 */
export function canAccessComponent(
  permissions: RolePermissions | undefined,
  pageId: string,
  componentId: string
): boolean {
  if (!permissions) return true;
  if (permissions.type === 'all') return true;
  const pages = permissions.pages;
  if (!Array.isArray(pages)) return false;
  const entry = pages.find((p) => p.pageId === pageId);
  if (!entry) return false;
  if (entry.components == null) return true;
  if (entry.components.length === 0) return false;
  if (entry.components.includes(FULL_PAGE_COMPONENT_ID)) return true;
  return entry.components.includes(componentId);
}
